/* eslint-disable @typescript-eslint/no-inferrable-types */
import { Type } from "class-transformer";
import { Competency } from "src/modules/enaio-certificates/shared/competency.model";
import { Pupil } from "src/modules/enaio-certificates/shared/pupil.model";
import { CustomInitializer } from "src/modules/sm-base/shared/custom-initializer.interface";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Map2 } from "src/modules/utils/shared/map2.model";
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { Column, Entity, OneToMany, PrimaryColumn } from "typeorm";
import { ConductGrade } from "./conduct-grade.entity";
import { Exam } from "./exam.entity";
import { GlobalSettings } from "./global-settings.entity";
import { GradeHistory } from "./grade-history.entity";
import { SchoolClass } from "./school-class.model";
import { SchoolTypeSettings } from "./school-type-settings.entity";

@Entity()
export class GradeBookSchoolClass implements CustomInitializer {
    @PrimaryColumn()
    id: number = 0;

    @Column({default: false})
    isHalfYear: boolean = false;

    @OneToMany(() => Exam, o => o.schoolClass)
    @Type(() => Exam)
    exams: Exam[];

    @OneToMany(() => ConductGrade, o => o.schoolClass)
    @Type(() => ConductGrade)
    conductGrades: ConductGrade[];

    @OneToMany(() => GradeHistory, o => o.schoolClass)
    @Type(() => GradeHistory)
    history: GradeHistory[];

    customInitializer(): void {
        this.exams = Arrays.ensure(this.exams);
        this.conductGrades = Arrays.ensure(this.conductGrades);
        this.history = Arrays.ensure(this.history);
        this.exams.forEach(e => e.customInitializer());
    }

    fillMissingGrades(pupils: Pupil[], conductCompetencies: Competency[]): void {
        for (let exam of this.exams) {
            exam.fillMissingGrades(pupils);
        }

        for (let pupil of pupils) {
            for (let competency of conductCompetencies) {
                if (!this.conductGrades.some(g => g.pupilId == pupil.id && g.competencyId == competency.id)) {
                    this.conductGrades.push(Objects.fromPlain(ConductGrade, { pupilId: pupil.id, competencyId: competency.id }));
                }
            }
        }
    }

    getConductGradeForPupil(pupilId: number, competencyId: number): ConductGrade {
        return this.conductGrades.find(g => g.pupilId == pupilId && g.competencyId == competencyId) || new ConductGrade();
    }

    getConductGradeMap(): { [key: string]: ConductGrade } {
        let result: { [key: string]: ConductGrade } = {};
        for (let g of this.conductGrades) {
            result[g.pupilId.toString() + "|" + g.competencyId.toString()] = g;
        }
        return result;
    }

    getExamsForSemester(globalSettings: GlobalSettings, grade: number, semester: number = null): Exam[] {
        return grade == 12 && semester != null ? this.exams.filter(e => semester == 1 && e.date < globalSettings.dateOfSemesterEnd || semester == 2 && e.date >= globalSettings.dateOfSemesterEnd) :
            grade == 11 && semester != null ? this.exams.filter(e => semester == 1 && e.date < globalSettings.dateOfHalfYearCertificate || semester == 2 && e.date >= globalSettings.dateOfHalfYearCertificate) : this.exams;
    }

    getPupilAverage(globalSettings: GlobalSettings, settings: SchoolTypeSettings, pupilId: number, schoolClass: SchoolClass, domain: string = null, semester: number = null, pupilData: Pupil = null): number {
        let count = 0;
        let sum = 0;
        let byDomain = Arrays.toMultiMapKeys(this.getExamsForSemester(globalSettings, schoolClass.grade, semester), e => e.domain);
        for (let d of Objects.getPropertyNames(byDomain)) {
            if (domain != null && d != domain) {
                continue;
            }
            let ds = settings.domainSettings.find(ds2 => ds2.domain == d);
            let byTypeId = Arrays.toMultiMapKeys(byDomain[d], e => e.examType == null ? "0" : Objects.toString(e.examType.id));
            let typeGgn = byDomain[d].find(e => e.examType?.title == "gGN")?.examType;
            let typeKlausur = byDomain[d].find(e => e.examType?.title == "Klausuren" || e.examType?.title == "Klassenarbeiten")?.examType;
            let ggnTypeIds = Arrays.withoutNull([typeGgn?.id, typeKlausur?.id]);
            let ratedGgnCount = byDomain[d].filter(exam => ggnTypeIds.includes(exam.examType?.id) && exam.grades.find(grade => grade.pupilId == pupilId)?.isRated()).length;
            let ggnFactor = settings.getGgnFactor(d, schoolClass.grade, pupilData, ratedGgnCount);
            let kgnFactor = settings.getKgnFactor(d, schoolClass.grade, pupilData, ratedGgnCount);
            for (let typeId of Objects.getPropertyNames(byTypeId)) {
                let factor = ds != null && typeId != null ? ds.getExamTypeFactor(Utils.toNumber(typeId)) : 1;
                if (schoolClass.usesExamTypeCalculation()) {
                    let tid = Utils.toNumber(typeId);
                    factor = tid == typeGgn?.id || tid == typeKlausur?.id ? ggnFactor : kgnFactor;
                }
                let subCount = 0;
                let subSum = 0;
                for (let exam of byTypeId[typeId]) {
                    let pupil = exam.grades.find(g => g.pupilId == pupilId);
                    if (pupil?.isRated()) {
                        subCount += 1;
                        subSum += pupil.grade;
                    }
                }
                if (subCount > 0) {
                    let subAvg = subSum / subCount;
                     count += factor;
                    sum += subAvg * factor;
                }
            }
        }

        return count == 0 ? null : sum / count;
    }

    getPupilAverageTextualGrade(globalSettings: GlobalSettings, settings: SchoolTypeSettings, pupilId: number, grade: number, schoolClass: SchoolClass, domain: string = null, pupilData: Pupil = null): number {
        let count = 0;
        let sum = 0;
        let gs = settings.getGradeSettings(grade);
        let byDomain = Arrays.toMultiMapKeys(this.exams, e => e.domain);
        for (let d of Objects.getPropertyNames(byDomain)) {
            if (domain != null && d != domain) {
                continue;
            }
            let ds = settings.domainSettings.find(ds2 => ds2.domain == d);
            let byTypeId = Arrays.toMultiMapKeys(byDomain[d], e => e.examType == null ? "0" : Objects.toString(e.examType.id));
            let typeGgn = byDomain[d].find(e => e.examType?.title == "gGN")?.examType;
            let typeKlausur = byDomain[d].find(e => e.examType?.title == "Klausuren" || e.examType?.title == "Klassenarbeiten")?.examType;
            let ggnTypeIds = Arrays.withoutNull([typeGgn?.id, typeKlausur?.id]);
            let ratedGgnCount = byDomain[d].filter(exam => ggnTypeIds.includes(exam.examType?.id) && exam.grades.find(grade2 => grade2.pupilId == pupilId)?.isRated()).length;
            let ggnFactor = settings.getGgnFactor(d, schoolClass.grade, pupilData, ratedGgnCount);
            let kgnFactor = settings.getKgnFactor(d, schoolClass.grade, pupilData, ratedGgnCount);
            for (let typeId of Objects.getPropertyNames(byTypeId)) {
                let factor = ds != null && typeId != null ? ds.getExamTypeFactor(Utils.toNumber(typeId)) : 1;
                if (schoolClass.usesExamTypeCalculation()) {
                    let tid = Utils.toNumber(typeId);
                    factor = tid == typeGgn?.id || tid == typeKlausur?.id ? ggnFactor : kgnFactor;
                }
                let subCount = 0;
                let subSum = 0;
                for (let exam of byTypeId[typeId]) {
                    let pupil = exam.grades.find(g => g.pupilId == pupilId);
                    if (pupil?.isRated()) {
                        let tg = gs.getTextualGrade(pupil.grade, true);
                        let g = tg == "" ? 0 : Utils.toNumber(tg);
                        subCount += 1;
                        subSum += g;
                    }
                }
                if (subCount > 0) {
                    let subAvg = subSum / subCount;
                    count += factor;
                    sum += subAvg * factor;
                }
            }
        }

        return count == 0 ? null : sum / count;
    }

    getPupilAverageOld(settings: SchoolTypeSettings, pupilId: number, domain: string = null): number {
        let count = 0;
        let sum = 0;
        for (let exam of this.exams) {
            if (domain != null && exam.domain != domain) {
                continue;
            }
            let ds = settings.domainSettings.find(ds2 => ds2.domain == exam.domain);
            let factor = ds != null && exam.examType != null ? ds.getExamTypeFactor(exam.examType.id) : 1;
            let pupil = exam.grades.find(g => g.pupilId == pupilId);
            if (pupil?.isRated()) {
                count += factor;
                sum += pupil.grade * factor;
            }
        }

        return count == 0 ? null : sum / count;
    }

    getPupilAverageTextualGradeOld(settings: SchoolTypeSettings, pupilId: number, grade: number, domain: string = null): number {
        let count = 0;
        let sum = 0;
        let gs = settings.getGradeSettings(grade);
        for (let exam of this.exams) {
            if (domain != null && exam.domain != domain) {
                continue;
            }
            let ds = settings.domainSettings.find(ds2 => ds2.domain == exam.domain);
            let factor = ds != null && exam.examType != null ? ds.getExamTypeFactor(exam.examType.id) : 1;
            let pupil = exam.grades.find(g => g.pupilId == pupilId);
            if (pupil?.isRated()) {
                let tg = gs.getTextualGrade(pupil.grade, true);
                let g = tg == "" ? 0 : Utils.toNumber(tg);
                count += factor;
                sum += g * factor;
            }
        }

        return count == 0 ? null : sum / count;
    }

    getPupilConductAverage(pupilId: number): number {
        let count = 0;
        let sum = 0;
        for (let g of this.conductGrades) {
            if (g.pupilId == pupilId && g.isRated()) {
                count++;
                sum += g.grade;
            }
        }

        return count == 0 ? null : sum / count;
    }

    getPupilAverageMap(globalSettings: GlobalSettings, settings: SchoolTypeSettings, schoolClass: SchoolClass, semester: number = null, pupilData: Pupil[] = []): OrdinaryObject<number> {
        let result: OrdinaryObject<number> = {};
        let domains = Arrays.getUnique(this.exams.map(e => e.domain));
        let pupils = Arrays.getUnique([...Arrays.explode(this.exams, e => e.grades).map(g => g.pupilId), ...this.conductGrades.map(g => g.pupilId)]);
        let byPupil = new Map2<number, number[]>();
        for (let pupil of pupils) {
            for (let domain of domains) {
                let avg = this.getPupilAverage(globalSettings, settings, pupil, schoolClass, domain, semester, pupilData.find(item => item.id == pupil));
                result[pupil + "|" + domain] = avg;
                if (avg > 0) {
                    byPupil.getOrAdd(pupil, () => []).push(avg);
                }
            }
            result[pupil + "|conduct"] = this.getPupilConductAverage(pupil);
        }
        for (let pupil of byPupil.keys()) {
            result[pupil + "|"] = Arrays.sum(byPupil.get(pupil)) / Math.max(1, byPupil.get(pupil).length);
        }
        return result;
    }

    getPupilAverageMapTextualGrade(globalSettings: GlobalSettings, settings: SchoolTypeSettings, grade: number, schoolClass: SchoolClass, semester: number = null, pupilData: Pupil[] = []): OrdinaryObject<number> {
        let result: OrdinaryObject<number> = {};
        let domains = Arrays.getUnique(this.getExamsForSemester(globalSettings, schoolClass.grade, semester).map(e => e.domain));
        let pupils = Arrays.getUnique([...Arrays.explode(this.exams, e => e.grades).map(g => g.pupilId), ...this.conductGrades.map(g => g.pupilId)]);
        let byPupil = new Map2<number, number[]>();
        for (let pupil of pupils) {
            for (let domain of domains) {
                let avg = this.getPupilAverageTextualGrade(globalSettings, settings, pupil, grade, schoolClass, domain, pupilData.find(item => item.id == pupil));
                result[pupil + "|" + domain] = avg;
                if (avg > 0) {
                    byPupil.getOrAdd(pupil, () => []).push(avg);
                }
            }
        }
        for (let pupil of byPupil.keys()) {
            result[pupil + "|"] = Arrays.sum(byPupil.get(pupil)) / Math.max(1, byPupil.get(pupil).length);
        }
        return result;
    }

}